import { Button } from "@/components/ui/button";
import { Can } from "@/components/Can";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X, Pencil, Plus } from "lucide-react";

export const CARD_STYLES = [
  { value: "default", label: "Default (Global)" },
  { value: "gradient-border", label: "Gradient Border" },
  { value: "top-accent", label: "Top Accent" },
  { value: "left-accent", label: "Left Accent" },
  { value: "right-accent", label: "Right Accent" },
  { value: "bottom-accent", label: "Bottom Accent" },
  { value: "glass", label: "Glass Card" },
  { value: "outlined", label: "Outlined" },
  { value: "ios", label: "iOS Style" },
  { value: "pill", label: "Pill Rounded" },
];

export const BORDER_COLORS = [
  { value: "default", label: "Default", color: "#9ca3af" },
  { value: "#ef4444", label: "Red", color: "#ef4444" },
  { value: "#f97316", label: "Orange", color: "#f97316" },
  { value: "#eab308", label: "Yellow", color: "#eab308" },
  { value: "#22c55e", label: "Green", color: "#22c55e" },
  { value: "#3b82f6", label: "Blue", color: "#3b82f6" },
  { value: "#8b5cf6", label: "Purple", color: "#8b5cf6" },
  { value: "#ec4899", label: "Pink", color: "#ec4899" },
  { value: "#000000", label: "Black", color: "#000000" },
];

export const GRADIENT_COLORS = [
  { value: "default", label: "Default", gradient: "linear-gradient(135deg, #9ca3af, #6b7280)" },
  { value: "linear-gradient(135deg, #ef4444, #f97316)", label: "Red to Orange", gradient: "linear-gradient(135deg, #ef4444, #f97316)" },
  { value: "linear-gradient(135deg, #f97316, #eab308)", label: "Orange to Yellow", gradient: "linear-gradient(135deg, #f97316, #eab308)" },
  { value: "linear-gradient(135deg, #22c55e, #3b82f6)", label: "Green to Blue", gradient: "linear-gradient(135deg, #22c55e, #3b82f6)" },
  { value: "linear-gradient(135deg, #3b82f6, #8b5cf6)", label: "Blue to Purple", gradient: "linear-gradient(135deg, #3b82f6, #8b5cf6)" },
  { value: "linear-gradient(135deg, #8b5cf6, #ec4899)", label: "Purple to Pink", gradient: "linear-gradient(135deg, #8b5cf6, #ec4899)" },
  { value: "linear-gradient(135deg, #ec4899, #ef4444)", label: "Pink to Red", gradient: "linear-gradient(135deg, #ec4899, #ef4444)" },
  { value: "linear-gradient(135deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #8b5cf6)", label: "Rainbow", gradient: "linear-gradient(135deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #8b5cf6)" },
  { value: "linear-gradient(135deg, #000000, #ef4444)", label: "Black to Red", gradient: "linear-gradient(135deg, #000000, #ef4444)" },
  { value: "linear-gradient(135deg, #000000, #3b82f6)", label: "Black to Blue", gradient: "linear-gradient(135deg, #000000, #3b82f6)" },
  { value: "linear-gradient(135deg, #000000, #22c55e)", label: "Black to Green", gradient: "linear-gradient(135deg, #000000, #22c55e)" },
  { value: "linear-gradient(135deg, #000000, #eab308)", label: "Black to Yellow", gradient: "linear-gradient(135deg, #000000, #eab308)" },
  { value: "linear-gradient(135deg, #000000, #f97316)", label: "Black to Orange", gradient: "linear-gradient(135deg, #000000, #f97316)" },
];

// "Product & Style" tab content of ButtonConfigDialog — extracted for readability.
// Purely presentational; all state stays owned by the parent dialog.
export function ProductStylePanel({
  displayName, setDisplayName,
  buttonImage, setButtonImage, imageUrlInput, setImageUrlInput,
  canGenerateAI, setShowAIDialog,
  cardStyle, setCardStyle, borderColor, setBorderColor,
  searchQuery, setSearchQuery, handleSearchKeyDown,
  selectedProduct, filteredProducts, categories, subCategories, groups, pickProduct, onEditProduct,
  handleSave, currentProduct, handleClear, onClose, subButtons, onUploadImage,
}) {
  return (
    <div className="space-y-3">
      {/* Display Name */}
      <div>
        <Label htmlFor="display-name" className="text-sm">Display Name</Label>
        <Input
          id="display-name"
          data-testid="button-config-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Product display name on button"
          className="mt-1 h-9"
        />
        <p className="text-xs text-zinc-500 mt-0.5">
          Leave blank to use product name
        </p>
      </div>

      {/* Image */}
      <div>
        <Label className="text-sm">Button Image (optional)</Label>
        <div className="flex items-center gap-2 mt-1">
          {buttonImage && (
            <div className="flex items-center gap-1">
              <img src={buttonImage} alt="Button" data-testid="button-config-product-image-preview" className="w-8 h-8 object-cover rounded border" onError={(e) => { e.target.style.display = "none"; }} />
              <button type="button" className="text-red-500 hover:text-red-700" title="Remove image" onClick={() => setButtonImage("")}><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <Input
            placeholder="Paste image URL..."
            data-testid="button-config-product-image-url"
            value={imageUrlInput}
            onChange={(e) => setImageUrlInput(e.target.value)}
            className="flex-1 h-8 text-xs"
          />
          <Button size="sm" className="h-8 text-xs" data-testid="button-config-product-set-image-url" disabled={!imageUrlInput.trim()} onClick={() => { setButtonImage(imageUrlInput.trim()); setImageUrlInput(""); }}>
            Set URL
          </Button>
          <Can k="products.button_config.choose_image"><Button variant="outline" size="sm" className="h-8 text-xs" data-testid="button-config-product-upload-image" onClick={onUploadImage}>
            Upload
          </Button></Can>
          {canGenerateAI && (
            <Can k="products.button_config.ai_image"><Button variant="outline" size="sm" className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="button-config-product-generate-ai" onClick={() => setShowAIDialog(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> +Image
            </Button></Can>
          )}
        </div>
      </div>

      {/* Style & Border - Compact Row */}
      <div className="flex gap-3">
        <div className="flex-1">
          <Label className="text-sm">Button Style</Label>
          <Select value={cardStyle} onValueChange={setCardStyle}>
            <SelectTrigger className="mt-1 h-9">
              <SelectValue placeholder="Default (Global)" />
            </SelectTrigger>
            <SelectContent>
              {CARD_STYLES.map((style) => (
                <SelectItem key={style.value} value={style.value}>
                  {style.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Border Color</Label>
          {(cardStyle === "default" || cardStyle === "gradient-border") ? (
            <>
              <div className="flex gap-1 mt-1 flex-wrap">
                {GRADIENT_COLORS.map((g) => (
                  <button
                    key={g.value}
                    onClick={() => setBorderColor(g.value)}
                    className={`w-6 h-6 rounded-md border-2 transition-all ${
                      borderColor === g.value
                        ? "border-blue-600 scale-110"
                        : "border-zinc-300 hover:border-zinc-400"
                    }`}
                    style={{ background: g.gradient }}
                    title={g.label}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-1 mt-1 flex-wrap">
                {BORDER_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setBorderColor(color.value)}
                    className={`w-6 h-6 rounded-md border-2 transition-all ${
                      borderColor === color.value
                        ? "border-blue-600 scale-110"
                        : "border-zinc-300 hover:border-zinc-400"
                    }`}
                    style={{ backgroundColor: color.color }}
                    title={color.label}
                  />
                ))}
              </div>
              {/* Color Picker and Hex Input */}
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="color"
                  value={borderColor === "default" ? "#9ca3af" : borderColor}
                  onChange={(e) => setBorderColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-zinc-300"
                  title="Pick custom color"
                />
                <Input
                  type="text"
                  value={borderColor === "default" ? "" : borderColor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || val === "default") {
                      setBorderColor("default");
                    } else if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                      setBorderColor(val);
                    }
                  }}
                  placeholder="#HEX"
                  className="w-24 h-8 text-sm font-mono"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Product Selection */}
      <div>
        <Label className="text-sm">Select Product <span className="text-zinc-400 font-normal">(search by name or scan SKU + Enter)</span></Label>
        <div className="relative mt-1 mb-2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
          <Input
            type="text"
            data-testid="button-config-product-search"
            placeholder="Search by name or scan SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-9 h-9"
          />
        </div>
        {selectedProduct && (
          <div className="mb-2 p-2 bg-blue-50 rounded-md border border-blue-200 flex items-center justify-between" data-testid="button-config-selected-product">
            <div>
              <div className="text-xs font-bold text-blue-800">{selectedProduct.name}</div>
              <div className="text-[10px] text-blue-600">SKU: {selectedProduct.sku}</div>
            </div>
            <span className="text-sm font-bold text-blue-700">${(selectedProduct.retail_price || selectedProduct.price || 0).toFixed(2)}</span>
          </div>
        )}

        <ScrollArea className="h-44 border border-zinc-200 rounded-md">
          <div className="p-2 space-y-1">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                data-testid={`button-config-product-option-${product.id}`}
                onClick={() => pickProduct(product)}
                className={`p-2 rounded-md cursor-pointer transition-colors ${
                  selectedProduct?.id === product.id
                    ? "bg-blue-100 border-blue-500 border"
                    : "hover:bg-zinc-100 border border-transparent"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-sm">{product.name}</p>
                    <p className="text-xs text-zinc-600">
                      {categories.find(c => c.id === product.category)?.name || ""}
                      {product.sub_category && subCategories.find(s => s.id === product.sub_category)?.name ? ` / ${subCategories.find(s => s.id === product.sub_category).name}` : ""}
                      {product.group && groups.find(g => g.id === product.group)?.name ? ` / ${groups.find(g => g.id === product.group).name}` : ""}
                      {" • "}SKU: {product.sku}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {onEditProduct && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditProduct(product);
                        }}
                        className="p-1 rounded hover:bg-blue-200 text-blue-600"
                        title="Edit Product"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <p className="font-bold text-blue-600 text-sm">${(product.retail_price || product.price || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))}
            {filteredProducts.length === 0 && (
              <div className="text-center py-6 text-zinc-500 text-sm">
                {searchQuery.trim().length < 3
                  ? `Type ${3 - searchQuery.trim().length} more character${3 - searchQuery.trim().length === 1 ? '' : 's'} to search, or scan a SKU...`
                  : "No products found"
                }
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button
          onClick={handleSave}
          data-testid="button-config-save"
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold h-10"
          disabled={!selectedProduct && subButtons.length === 0}
        >
          Save Configuration
        </Button>
        {currentProduct && (
          <Button
            onClick={handleClear}
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 h-10"
          >
            <X className="w-4 h-4 mr-1" />
            Delete
          </Button>
        )}
        <Button onClick={onClose} variant="outline" className="h-10">
          Cancel
        </Button>
      </div>
    </div>
  );
}
